import { assertNever, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import {
    asArrayValue,
    asBoolean,
    asString,
    isLoadedDefined,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
} from "common/components/component-helpers";
import { areValuesEqual } from "common/components/primitives";
import type { ForceValueProducer, PotentialValue, Value } from "common/components/types";
import { ActionSource, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { ChoiceStyle } from "component-utils/choice-style";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import sortBy from "lodash/sortBy";
import { useCallback } from "react";
import ChipsView from "webapp/ui/views/chips/chips-view";
import RadioButtonsView from "webapp/ui/views/radio-buttons/radio-buttons-view";
import Dropdown from "../ui/views/dropdown/dropdown";
import InlinePivotView from "../ui/views/inline-pivot/inline-pivot-view";

interface Choice {
    readonly name: string;
    readonly value: Value;
}

function makeChoices(choicesPV: PotentialValue, force: ForceValueProducer): Choice[] {
    const results: Choice[] = [];
    for (const choicePV of loadedDefinedMap(choicesPV, asArrayValue) ?? []) {
        const choiceInspector = choicePV.getObjectInspector(force);
        if (choiceInspector === LOADING) continue;

        if (choiceInspector !== undefined) {
            const name = definedMap(choiceInspector.getProperty("name"), force);
            const value = definedMap(choiceInspector.getProperty("value"), force);

            if (!isLoadedDefined(name) || !isLoadedDefined(value)) continue;

            results.push({ name: asString(name), value });
        } else {
            const choiceValue = force(choicePV);
            if (!isLoadedDefined(choiceValue)) continue;

            results.push({ name: asString(choiceValue), value: choiceValue });
        }
    }

    return results;
}

const multiChoiceSeparator = ",";

export const ChoiceMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const { appKind } = env.appEnvironment;
    const [force] = useForce();
    const [
        [valuePV, choicesPV, captionPV, onChangePV, isEnabledPV, isRequiredValue, stylePV, isMultiPV, pushScreenPV],
        [valueProducer],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const { onClick: onChangeCB } = useOnClickFromAction(onChangePV, ActionSource.Switch, ctx, env);
    const { onClick: onPushCB } = useOnClickFromAction(pushScreenPV, ActionSource.Switch, ctx, env);
    const [editingAllowed] = useEditComponentEnabledViaNetworkStatus(env);

    const isMulti = definedMap(isMultiPV, asBoolean) ?? false;
    const choices = makeChoices(choicesPV, force);
    const isRequired = isRequiredValue === true;
    if (!isRequired && !isMulti) {
        choices.unshift({ name: "—", value: undefined });
    }

    // `choices` is a `Value[]`, but `value` is a `PotentialValue`.
    // Take note of this areValuesEqual here: the potential value might not actually be
    // a JavaScript primitive!
    let current: number[];

    if (isMulti) {
        // FIXME: This will suck hard for large cases.
        const valueStr = asString(valuePV)
            .split(multiChoiceSeparator)
            .map(x => x.trim())
            .filter(x => x !== "");
        current = valueStr.map(v => choices.findIndex(c => areValuesEqual(v, c.value))).filter(x => x !== -1);
    } else {
        const found = choices.findIndex(
            c => areValuesEqual(valuePV, c.value) || (valuePV === "" && c.value === undefined)
        );
        current = found === -1 ? [] : [found];
    }

    const onChange = useCallback(
        (index: number): void => {
            if (valueProducer === undefined) return;

            let indexes: number[];
            if (isMulti) {
                if (current.includes(index)) {
                    indexes = current.filter(x => x !== index);
                } else {
                    indexes = [...current, index];
                }
            } else {
                indexes = [index];
            }

            const sorted = sortBy(indexes);
            const values = mapFilterUndefined(sorted, i => choices[i]?.value);
            let newValue: Value;

            if (values.length === 0) {
                // We can't use `undefined`
                newValue = "";
            } else if (values.length === 1) {
                newValue = values[0];
            } else {
                newValue = values.map(asString).join(multiChoiceSeparator);
            }

            valueProducer.set(newValue);
            if (onChangeCB !== undefined) {
                void onChangeCB(undefined);
            }
        },
        [choices, current, isMulti, onChangeCB, valueProducer]
    );

    if (valuePV === LOADING || captionPV === LOADING || choicesPV === undefined || isMultiPV === LOADING) {
        return null;
    }

    if (choicesPV === LOADING) return env.renderProgress();

    const style = loadedDefinedMapWithDefault(stylePV, ChoiceStyle.Dropdown, asString) as ChoiceStyle;

    const onPushScreen = onPushCB !== undefined ? () => onPushCB(undefined) : undefined;
    const isEnabled = editingAllowed && (onChangeCB === undefined || isEnabledPV !== false);

    const choicesStrings = choices.map(c => c.name);

    if (style === ChoiceStyle.Dropdown) {
        return (
            <Dropdown
                renderEnvironment={env}
                isEnabled={isEnabled}
                isRequired={isRequired}
                items={choicesStrings}
                onSelect={onChange}
                current={current}
                title={asString(captionPV)}
                onPushScreen={isEnabled ? onPushScreen : undefined}
                appKind={appKind}
            />
        );
    } else if (style === ChoiceStyle.Inline) {
        return (
            <InlinePivotView
                title={asString(captionPV)}
                items={choicesStrings}
                current={current[0]}
                onClick={i => onChange(i)}
                isRequired={isRequired}
                appKind={appKind}
            />
        );
    } else if (style === ChoiceStyle.Chips) {
        return (
            <ChipsView
                title={asString(captionPV)}
                appKind={appKind}
                items={choicesStrings}
                current={current}
                onClick={onChange}
                isRequired={isRequired}
            />
        );
    } else if (style === ChoiceStyle.RadioButtons) {
        return (
            <RadioButtonsView
                isMulti={isMulti}
                title={asString(captionPV)}
                appKind={appKind}
                items={choicesStrings}
                current={current}
                onClick={onChange}
                isRequired={isRequired}
            />
        );
    } else {
        return assertNever(style);
    }
};
